import { useNavigate } from 'react-router-dom'
import { useAccessibility } from '../../../hooks/useAccessibility'
import { useApp } from '../../../context/App'
import { FacebookIcon } from '../../../components/ui/icons/social/facebook'
import { InstagramIcon } from '../../../components/ui/icons/social/instagram'
import { YoutubeIcon } from '../../../components/ui/icons/social/youtube'
import { FlickrIcon } from '../../../components/ui/icons/social/flickr'
import { PhoneIcon } from '../../../components/ui/icons/common/phone'
import { SinaisIcon } from '../../../components/ui/icons/acessibilidade/sinais'
import { AccessibilidadeIcon } from '../../../components/ui/icons/acessibilidade/accessibilidade'
import { Sun, Moon, Plus, Minus, RotateCcw } from 'lucide-react'
import React from 'react'

const TopNavLink = ({ label, url, onClick }: { label: string, url?: string, onClick?: () => void }) => {
	const className = "text-white uppercase text-[0.625rem] lg:text-[0.6875rem] font-bold tracking-wider hover:text-secondary transition-colors focus-visible:ring-2 focus-visible:ring-secondary focus-visible:outline-none rounded-sm px-1";
	if (onClick) {
		return <button onClick={onClick} className={`bg-transparent border-none cursor-pointer ${className}`}>{label}</button>;
	}
	return <a href={url} target="_blank" rel="noreferrer noopener" className={`no-underline ${className}`}>{label}</a>;
};

const FontControlBtn = ({ onClick, disabled, ariaLabel, title, children }: { onClick: () => void, disabled?: boolean, ariaLabel: string, title?: string, children: React.ReactNode }) => (
	<button
		className="bg-transparent border-none cursor-pointer flex items-center p-1.5 text-white hover:text-secondary transition-colors disabled:opacity-30 disabled:cursor-not-allowed focus-visible:ring-2 focus-visible:ring-secondary focus-visible:outline-none rounded-full"
		onClick={onClick}
		disabled={disabled}
		aria-label={ariaLabel}
		title={title}
	>
		{children}
	</button>
);

const SocialLink = ({ url, label, icon }: { url: string, label: string, icon: React.ReactNode }) => (
	<a
		href={url}
		target="_blank"
		rel="noreferrer noopener"
		className="text-white hover:text-secondary transition-all duration-300 hover:-translate-y-0.5 focus-visible:ring-2 focus-visible:ring-secondary focus-visible:outline-none rounded-sm p-1"
		aria-label={label}
	>
		<span className="[&>svg]:w-4 [&>svg]:h-4 [&>svg]:fill-current flex" aria-hidden="true">
			{icon}
		</span>
	</a>
);

export function ToolBar() {
	const navigate = useNavigate()
	const {
		toggleTheme,
		isDark,
		increaseFontSize,
		decreaseFontSize,
		resetFontSize,
		fontLevel,
		canIncrease,
		canDecrease
	} = useAccessibility()
	const { mobile, setVisible, visible, getTenantUrl, tenant } = useApp()
	const isDefaultTenant = tenant.id === 'municipio'

	if (mobile) {
		return (
			<div className="flex flex-col w-full bg-primary-dark dark:bg-gray-900 transition-colors duration-300">
				<div className="flex w-full h-12 items-center justify-center box-border border-b border-white/10 lg:hidden focus-within:ring-2 focus-within:ring-secondary">
					<div className="flex w-11/12 items-center justify-between">
						<div className="flex items-center">
							<a href="tel:+4733416000" className="flex items-center gap-2 text-white no-underline text-xs font-bold transition-opacity hover:opacity-80">
								<span className="[&>svg]:w-4 [&>svg]:h-4 [&>svg]:fill-white flex" aria-hidden="true">
									<PhoneIcon />
								</span>
								<span>(47) 3341-6000</span>
							</a>
						</div>

						<div className="flex items-center gap-3">
							{/* Acessibilidade */}
							<a
								href={getTenantUrl("/a/177")}
								className="text-white flex items-center p-1.5 transition-transform hover:scale-110"
								aria-label="Página de acessibilidade"
							>
								<span className="[&>svg]:h-5 [&>svg]:fill-white flex" aria-hidden="true">
									<AccessibilidadeIcon />
								</span>
							</a>

							{/* Dark mode toggle */}
							<button
								className="bg-transparent border-none text-white flex items-center p-1.5 cursor-pointer transition-transform hover:scale-110"
								onClick={toggleTheme}
								aria-label={isDark ? 'Ativar modo claro' : 'Ativar modo escuro'}
							>
								{isDark ? <Sun size={18} aria-hidden="true" /> : <Moon size={18} aria-hidden="true" />}
							</button>

							{/* Font controls */}
							<div className="flex items-center gap-0.5 bg-white/10 rounded-full px-1 py-0.5">
								<FontControlBtn onClick={decreaseFontSize} disabled={!canDecrease} ariaLabel="Diminuir tamanho da fonte" title="A-">
									<Minus size={14} aria-hidden="true" />
								</FontControlBtn>
								<FontControlBtn onClick={resetFontSize} ariaLabel="Restaurar tamanho original da fonte" title={`Nível: ${fontLevel}`}>
									<RotateCcw size={14} aria-hidden="true" />
								</FontControlBtn>
								<FontControlBtn onClick={increaseFontSize} disabled={!canIncrease} ariaLabel="Aumentar tamanho da fonte" title="A+">
									<Plus size={14} aria-hidden="true" />
								</FontControlBtn>
							</div>
						</div>
					</div>
				</div>
			</div>
		)
	}

	return (
		<div className="hidden lg:flex w-full h-10 items-center justify-center bg-primary-dark dark:bg-gray-900 border-b border-white/10 transition-colors duration-300">
			<div className="flex flex-row w-[85%] max-w-7xl h-full items-center justify-between">
				{/* Main Links */}
				<nav className="flex items-center gap-4 lg:gap-6 text-white uppercase text-[0.625rem] lg:text-[0.6875rem] font-bold tracking-wider" aria-label="Links institucionais">
					{[
						{ label: 'Ouvidoria', url: 'https://servicos.itajai.sc.gov.br/servico/79-ouvidoria.html' },
						{ label: 'e-SIC', url: 'https://servicos.itajai.sc.gov.br/servico/69-acesso_a_informacao.html' },
						{ label: 'Telefones', onClick: () => navigate(getTenantUrl('/contato')) },
						{ label: 'Diário Oficial', url: 'https://intranet2.itajai.sc.gov.br/jornal-municipio/portal/' },
						{ label: 'Transparência', url: 'https://portaltransparencia.itajai.sc.gov.br/epublica-portal/#/itajai/portal?entidade=411' }
					].map((item, idx) => (
						<TopNavLink key={idx} label={item.label} url={item.url ? getTenantUrl(item.url) : undefined} onClick={item.onClick} />
					))}
				</nav>

				{/* Accessibility & Tools */}
				<div className="flex items-center gap-4">
					{/* Accessibility Controls */}
					<div className="flex items-center gap-2 border-x border-white/10 px-4" role="toolbar" aria-label="Ferramentas de acessibilidade">
						{/* VLibras */}
						<button
							className="bg-transparent border-none cursor-pointer flex items-center p-1.5 hover:scale-110 transition-transform text-white hover:text-amarelo_itajai focus-visible:ring-2 focus-visible:ring-secondary focus-visible:outline-none rounded-md"
							onClick={() => setVisible(!visible)}
							aria-label={visible ? 'Desativar VLibras' : 'Ativar VLibras'}
							aria-pressed={visible}
						>
							<span className="[&>svg]:h-4 [&>svg]:fill-current flex" aria-hidden="true"><SinaisIcon /></span>
						</button>

						{/* Theme Toggle */}
						<button
							className="bg-transparent border-none cursor-pointer flex items-center p-1.5 hover:scale-110 transition-all text-white hover:text-amarelo_itajai focus-visible:ring-2 focus-visible:ring-secondary focus-visible:outline-none rounded-md"
							onClick={toggleTheme}
							aria-label={isDark ? 'Ativar modo claro' : 'Ativar modo escuro'}
							aria-pressed={isDark}
						>
							{isDark ? <Sun size={16} aria-hidden="true" /> : <Moon size={16} aria-hidden="true" />}
						</button>

						{/* Font Size Controls */}
						<div className="flex items-center gap-0.5 bg-white/5 rounded-full px-1 py-0.5 ml-2" role="group" aria-label="Controle de tamanho de fonte">
							<FontControlBtn onClick={decreaseFontSize} disabled={!canDecrease} ariaLabel="Diminuir fonte" title="A-">
								<Minus size={14} aria-hidden="true" />
							</FontControlBtn>
							<FontControlBtn onClick={resetFontSize} ariaLabel="Restaurar tamanho original" title={`Nível atual: ${fontLevel}`}>
								<RotateCcw size={14} aria-hidden="true" />
							</FontControlBtn>
							<FontControlBtn onClick={increaseFontSize} disabled={!canIncrease} ariaLabel="Aumentar fonte" title="A+">
								<Plus size={14} aria-hidden="true" />
							</FontControlBtn>
						</div>
					</div>

					{/* Social Links */}
					<nav className="flex items-center gap-3" aria-label="Redes sociais">
						{[
							{ icon: <FacebookIcon />, url: 'https://www.facebook.com/PrefeituradeItajai', label: 'Facebook' },
							{ icon: <InstagramIcon />, url: 'https://www.instagram.com/prefeituradeitajai/', label: 'Instagram' },
							{ icon: <YoutubeIcon />, url: 'https://www.youtube.com/MunicipiodeItajai', label: 'YouTube' },
							{ icon: <FlickrIcon />, url: 'https://www.flickr.com/photos/secomitajai/sets/', label: 'Flickr' }
						].map((item, idx) => (
							<SocialLink key={idx} {...item} />
						))}
					</nav>
				</div>
			</div>
		</div>
	)
}
